import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Layout, Table, Text} from '@phonectas/unifon-ui-kit';
import {LIMIT_PAYLOAD} from 'common/constants';
import ZendeskTableRow from './ZendeskTableRow';
import TableDialog from '../TableDialog';
import useThreeSixtyNewStore from 'common/stores/threeSixtyStore';
import {shallow} from 'zustand/shallow';
import {StyledSectionDiv} from '../../styled';
import {ZendeskCase} from 'common/types/store/threeSixtyTypes';
import ZendeskContent from './dialogContent/ZendeskContent';

interface ZendeskTableProps {
	id: string;
}

const ZendeskTable: React.FC<ZendeskTableProps> = ({id}) => {
	const [t] = useTranslation();
	const {getZendesk} = useThreeSixtyNewStore((state) => ({getZendesk: state.getZendesk}), shallow);
	const {zendesk, totalCount, accountNotFound} = useThreeSixtyNewStore((state) => ({
		zendesk: state.accountTables?.zendeskCases,
		totalCount: state.accountTables?.zendeskCases?.total_count,
		accountNotFound: state.accountNotFound,
	}), shallow);
	const [showDialog, setShowDialog] = useState(false);
	const [activeRow, setActiveRow] = useState<ZendeskCase | null>(null);
	const zendeskData = zendesk?.zendesk_cases;

	const onHideDialog = useCallback(() => {
		setShowDialog(false);
		setActiveRow(null);
	}, []);

	const handleRowClick = useCallback((item: ZendeskCase) => {
		setActiveRow(item);
		setShowDialog(true);
	}, []);

	const onLoadMore = useCallback((size: number) => {
		if (totalCount && totalCount <= size) return;
		getZendesk({
			id: id,
			limit: size + LIMIT_PAYLOAD,
		});
	}, [id, totalCount, getZendesk]);

	const renderItem = useCallback((item: ZendeskCase, index: number) => (
		<ZendeskTableRow
			key={index}
			data={item}
			onClick={handleRowClick}
		/>
	), [handleRowClick]);

	useEffect(() => {
		if (accountNotFound) return;
		if (totalCount === 0 && zendesk?.zendesk_cases === null && zendesk?.filters?.account_id === id) return;

		const hasZendeskData = Array.isArray(zendeskData) && zendeskData.length > 0;
		const firstAccountId = hasZendeskData ? zendeskData[0]?.account_id : undefined;
		const isAccountIdMatched = hasZendeskData && id === firstAccountId;
		const isZendeskFetched = zendesk !== null && isAccountIdMatched;
		const isZendeskFilterMatched = zendesk?.filters?.account_id === id && !hasZendeskData;

		if (isZendeskFetched || isZendeskFilterMatched) return;

		getZendesk({
			id: id,
			limit: LIMIT_PAYLOAD,
		});
	}, [id, zendesk, zendeskData, accountNotFound, getZendesk, totalCount]);

	const zendeskHeader = useMemo(() => {
		return (
			<Layout gap="sm">
				<Layout
					gap="xs"
				>
					<Text color="secondary">
						{`${t('Zendesk Cases')}:`}
					</Text>
					<Text>
						{`${activeRow?.case_id ? activeRow.case_id : ''} `}
					</Text>
				</Layout>
			</Layout>
		)
	}, [t, activeRow]);

	if (!zendesk || !zendesk?.zendesk_cases) {
		return (
			<StyledSectionDiv $endDiv $fullHeight>
				<Layout
					fullHeight
					fullWidth
					alignItems="center"
					justifyContent="center"
				>
					<Text>{t('No data')}</Text>
				</Layout>
			</StyledSectionDiv>
		);
	}

	return (
		<StyledSectionDiv
			style={{
				display: 'flex',
				flexDirection: 'column',
				flexGrow: '1',
				overflow: 'hidden',
				padding: '16px 8px'
			}}
			$endDiv
			$fullHeight
		>
			<Table
				items={zendeskData || []}
				renderItem={(item, index) => renderItem(item, index)}
				// rowHeight={70}
				virtualized
				onScrollDown={onLoadMore}
			>
				<Table.Row style={{padding: '0px'}}>
					<Table.Cell width={85}>{t('#Ticket')}</Table.Cell>
					<Table.Cell width={60}>{t('Status')}</Table.Cell>
					<Table.Cell>{t('Subject')}</Table.Cell>
					<Table.Cell>{t('Client')}</Table.Cell>
					<Table.Cell>{t('Owner')}</Table.Cell>
					<Table.Cell>{t('Queue')}</Table.Cell>
					<Table.Cell width={50}>{t('SLA')}</Table.Cell>
					<Table.Cell width={75}>{t('Updated')}</Table.Cell>
					<Table.Cell width={75}>{t('Created')}</Table.Cell>
				</Table.Row>
			</Table>
			<TableDialog
				show={showDialog}
				width={700}
				onClose={onHideDialog}
				title={zendeskHeader}
				noGap
			>
				<ZendeskContent data={activeRow} />
			</TableDialog>
		</StyledSectionDiv>
	);
}

export default ZendeskTable;
